import { getBlockType, LocalBlockTypeType } from "@pathwright/blocks-core"
import {
  BlockCompletionProvider,
  BlockDataType,
  BlockType,
  BlockValidationIndicator,
  useBlocksConfig
} from "@pathwright/blocks-engine"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import classnames from "classnames"
import { memo, useMemo } from "react"
import styled from "styled-components"
import BlockWrap from "../block/BlockWrap"
import { getBlock } from "../block/utils"

const requiresInternetConection = (
  blockType: LocalBlockTypeType | undefined
) => {
  if (!blockType) return false
  // For now this is any type of progress tracking block but that may not be the case
  // in the future
  return typeof blockType.resolveUserCompletion === "function"
}

const ReadOnlyOverlay = styled.div`
  width: 100%;
  height: 100%;
  left: 0px;
  padding: 10px;
  top: 0px;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: absolute;
  font-size: 16px;
  color: #fff;
  border-bottom: 1px solid #000;
  background-color: rgba(0, 0, 0, 0.66);
`

const propsAreEqual = (
  prevProps: BlockViewerProps,
  nextProps: BlockViewerProps
) => {
  if (prevProps.blockIndex !== nextProps.blockIndex) return false
  if (prevProps.block !== nextProps.block) return false

  return true
}

type BlockViewerProps = {
  block: BlockType
  onChange: (id: string, data: BlockDataType, trackProgress?: boolean) => void
  blockIndex: number
}
const BlockViewer = memo((props: BlockViewerProps) => {
  const { block, onChange, blockIndex } = props
  const { tc } = useTranslate()

  const { completion, type, layout, id, style, data } = block

  const { offline, disableInput } = useBlocksConfig()

  const blockType = useMemo(() => getBlockType(type), [type])
  const Block = useMemo(() => getBlock(type, "view"), [type])

  const handleChange = (dataChange: BlockDataType, trackProgress = false) => {
    const nextData = { ...data, ...dataChange }
    onChange(id, nextData, trackProgress)
  }

  // TODO: return a loading skeleton here
  if (!Block) return <div />

  const readOnly = offline && requiresInternetConection(blockType)

  // very temporary and brittle solution for custom styling
  const className = classnames(
    "ContentBlock",
    `ContentBlockType-${type}`,
    `ContentBlockLayout-${layout}`
  )

  const blockCompletionClass =
    completion && completion.progress !== null
      ? completion.progress
        ? " block-completed"
        : " block-incomplete"
      : ""

  return (
    <BlockCompletionProvider completion={completion} data={data}>
      <BlockWrap
        className={`Block-${blockIndex}${blockCompletionClass}`}
        styledColor={style?.styleAttributes.color}
        style={{
          ...(style?.styleAttributes || {}),
          transition: "all 0.5s"
        }}
        block={block}
      >
        <BlockValidationIndicator />
        {block.answeredContentUnavailable ? (
          <div role="status" style={{ padding: 20 }}>
            {tc(
              "The original question is unavailable. Your saved response has been retained."
            )}
            {completion?.score != null && (
              <p>
                {tc("Recorded score: {{score}}%", {
                  score: Math.round(completion.score * 100)
                })}
              </p>
            )}
          </div>
        ) : (
          <Block
            className={className}
            onChange={handleChange}
            layout={layout}
            completion={completion}
            blockType={blockType}
            readOnly={readOnly}
            id={id}
            data={data}
            blockStyles={style}
          />
        )}
        {completion?.pendingReset &&
          !disableInput &&
          !["question", "annotatedassessment"].includes(type.toLowerCase()) && (
            <button onClick={() => onChange(id, data, true)}>
              Submit saved response
            </button>
          )}
        {readOnly ? (
          <ReadOnlyOverlay>
            <Pathicon icon="lock" style={{ fontSize: 24 }} />
            <p>Please connect to the internet to access this content.</p>
          </ReadOnlyOverlay>
        ) : null}
      </BlockWrap>
    </BlockCompletionProvider>
  )
}, propsAreEqual)

BlockViewer.displayName = "BlockViewer"

export default BlockViewer
